/**
 * Connect a system — and, where the client has to arrange something first, say what.
 *
 * ── The half a "Connect X" button usually leaves out ────────────────────────────────────────
 *
 * Pressing Connect fails for reasons that are **not the same kind of thing**, and a button that
 * cannot tell them apart produces an opaque provider error the reader cannot attribute. So this
 * screen separates them, per provider:
 *
 *   • whether it can be connected **right now** — which needs both a complete connector *and* our
 *     own credentials to exist, and those are different facts;
 *   • the conditions the **client** has to satisfy in their own system before the grant will take —
 *     who may approve it, which portal or tenant it lands in;
 *   • when we are not ready, that it is **ours** and there is nothing for them to do.
 *
 * ⚠️ **Nothing here asks a client to register an OAuth app or hand us a secret**, and the omission
 * is the design rather than an oversight. Per
 * [ADR-079](../../../../docs/platform/11-technical-architecture.md#8--architecture-decision-records)
 * Dedupr registers one app per provider per environment and every client consents to it; option (b),
 * each client registering their own, was weighed and rejected — partly because it cannot work
 * without a client-secret field, which [F01's ui-spec](../../../../docs/features/01-onboarding-connections/ui-spec.md)
 * forbids in as many words: *"a field for it would be a field that puts it in a browser."*
 *
 * ⚠️ This screen implemented the rejected option until 2026-08-09 — it predates the ADR by a day and
 * was never reconciled, so the app shipped two screens disagreeing about who registers the app.
 */
import { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Callout, Card, Checkbox, EmptyState, HelpText, Skeleton } from '@dedupr/ui';
import { accessToken } from '../auth.js';
import { startConnect } from '../connectFlow.js';
import { revealSection } from '../revealSection.js';
import { InstallOffer } from '../InstallOffer.js';
import './Connect.css';

/** A condition the CLIENT has to satisfy in their own system — never our setup work (ADR-079). */
interface ClientPrerequisiteView {
  title: string;
  detail: string;
  who: 'you' | 'admin';
  link?: string;
}

interface ProviderView {
  providerId: string;
  providerName: string;
  transport: 'rest' | 'file';
  can_connect_now: boolean;
  serverReady: boolean;
  /** ⚠️ Our own env-var names. Present in the payload, never rendered on a client screen — see below. */
  serverMissing: string[];
  callback_url: string | null;
  /** ⚠️ Optional for version skew, not because the route omits it — see the render below. */
  client_prerequisites?: ClientPrerequisiteView[];
  /**
   * A limit on **OUR** side, in our own voice — B-315.
   *
   * ⚠️ Distinct from `client_prerequisites`, which are conditions in the client's own org that they
   * can act on. This is the opposite: something they cannot change and must not be asked to. Google
   * gates the **app**, not the credential, so until our Drive app clears verification only accounts
   * we have added as test users can consent at all.
   */
  our_limit?: string | null;
  /** Whether this provider's descriptor can enumerate custom objects (FR-4.8). */
  supports_custom_objects?: boolean;
  /** Whether this provider declares a sandbox host, so the choice is offered only where it exists. */
  supports_sandbox?: boolean;
}

interface ConnectionView {
  id: string;
  provider: string;
  connectedAt: number;
  needsReconnect: boolean;
}

function ProviderCard({
  provider,
  connections,
  onConnect,
  sandbox,
  onSandboxChange,
}: {
  provider: ProviderView;
  connections: ConnectionView[];
  /** Starts the OAuth flow. Passed in so the card holds no fetch of its own. */
  onConnect: (providerId: string) => Promise<void>;
  /** Whether the person has said this is a sandbox. Controlled by the screen, per provider. */
  sandbox: boolean;
  onSandboxChange: (next: boolean) => void;
}) {
  const mine = connections.filter((c) => c.provider === provider.providerId);

  return (
    <Card
      title={provider.providerName}
      headingLevel={3}
      action={
        provider.transport === 'file' ? (
          /* `#/sources` + reveal, not `#/local` — that route was deleted with the 2026-08-06
             reshape and this button kept pointing at it, landing on the Dashboard. The intake is
             on this very screen in local mode, so revealing it is the honest target. */
          <Button onClick={() => { window.location.hash = '#/sources'; revealSection('sources-intake'); }}>
            Upload a file
          </Button>
        ) : provider.can_connect_now ? (
          /* ⚠️ Was `window.location.href = '/api/relay/connect/…'`, a top-level navigation. That
             cannot carry an `Authorization` header, and since B-133 the route requires one — so the
             flow is now: POST with the token, receive the provider's authorize URL, then navigate.
             The consent screen still happens in the top-level window, which is the part that
             genuinely has to. */
          <Button onClick={() => void onConnect(provider.providerId)}>
            Connect {provider.providerName}
          </Button>
        ) : null
      }
    >
      {/* ⚠️ **Offered only where the descriptor declares a sandbox host**, and read from the payload
          rather than from a provider-id check — the same rule the Worker follows for `hostOverride`.
          Placed ABOVE the status and the button because it changes where the consent screen goes:
          a control that only matters before you press Connect belongs before it.
          Without this the flag was unreachable: the Worker has honoured `?sandbox=1` since the
          descriptor gained `sandboxHost`, and no screen ever sent it — so a client trying a sandbox
          first, which is the sensible order, got an `invalid_grant` naming neither host. */}
      {provider.supports_sandbox && provider.can_connect_now && mine.length === 0 ? (
        <div className="web-connect__sandbox">
          <Checkbox
            checked={sandbox}
            onChange={(e) => onSandboxChange(e.currentTarget.checked)}
            label={`This is a ${provider.providerName} sandbox`}
          />
          <HelpText>
            A sandbox signs in at a different address than production. Tick this for a sandbox, a
            scratch org or a developer org — leave it clear for your live one. It cannot be changed
            afterwards without reconnecting, because it is part of what you approve.
          </HelpText>
        </div>
      ) : null}

      <div className="web-connect__status">
        {mine.length > 0 ? (
          <Badge variant={mine.some((c) => c.needsReconnect) ? 'warning' : 'success'}>
            {mine.some((c) => c.needsReconnect) ? 'Reconnect needed' : `Connected (${mine.length})`}
          </Badge>
        ) : provider.transport === 'file' ? (
          <Badge variant="neutral">No setup needed</Badge>
        ) : provider.can_connect_now ? (
          <Badge variant="neutral">Ready to connect</Badge>
        ) : (
          /* Not "unavailable". The reason is known, and hiding it makes a solvable setup step look
             like a missing feature. */
          <Badge variant="warning">Needs setup first</Badge>
        )}
      </div>

      {provider.transport === 'file' ? (
        <HelpText>
          Read in this browser and never sent anywhere — so there is no app to create and no access to
          grant. This is the fastest way to see what we find.
        </HelpText>
      ) : null}

      {/* Custom-object mapping (FR-4.8, G2-7): only where a connection exists AND the descriptor can
          enumerate custom objects — an always-rendered link that usually 501s would teach people the
          feature is broken. One button per connection, because two Salesforce orgs have different
          objects and a link that silently picked one would map the wrong system's data. */}
      {provider.supports_custom_objects && mine.length > 0 ? (
        <div className="web-connect__custom">
          {mine.map((conn) => (
            <Button
              key={conn.id}
              variant="secondary"
              size="sm"
              onClick={() => {
                window.location.hash = `#/sources/custom-objects?connection=${encodeURIComponent(conn.id)}`;
              }}
            >
              {mine.length === 1
                ? 'Custom objects'
                : `Custom objects (connected ${new Date(conn.connectedAt).toLocaleDateString()})`}
            </Button>
          ))}
        </div>
      ) : null}

      {/* ⚠️ **This said the opposite until 2026-08-09, and it contradicted an ADR by name.** It read:
          *"This deployment is missing HUBSPOT_CLIENT_ID and HUBSPOT_CLIENT_SECRET. Once you have
          created the app below, send us the client id and secret and we will set them"* — which is
          [ADR-079](../../../../docs/platform/11-technical-architecture.md#8--architecture-decision-records)'s
          **rejected option (b)**, each client registering their own app, written as instructions on
          the one screen that ADR names. ADR-079 chose (a): Dedupr registers one app per provider per
          environment and every client consents to it. A client has no app to create and no secret to
          send, so the old copy sent somebody into their own console looking for our work.

          The screen predates the ADR (2026-08-07) and was never reconciled — so this app shipped two
          screens disagreeing about who registers the OAuth app, with the newer wizard correct.
          Our env-var names are gone with it: they mean nothing to a client and read as a thing they
          are meant to supply. They live on `admin.`, where somebody can act on them. */}
      {!provider.can_connect_now && provider.transport === 'rest' ? (
        <Callout tone="info" title="One step is ours, not yours">
          We have not finished setting {provider.providerName} up yet. This is our work — there is no
          app for you to create and nothing for you to send us — and we will tell you when it is
          ready.
        </Callout>
      ) : null}

      {/* ⚠️ **`provider.steps` is the OPERATOR's guide and is deliberately not rendered here.** Its
          first step is *"Create an app in HubSpot … you will get a client id and a client secret at
          the end of it"*, which under ADR-079 is Dedupr's work, done once per provider per
          environment — not something a client does, and not something they can do.

          What a client genuinely has to arrange is a different and much shorter list:
          `client_prerequisites`, conditions on *who* may grant access (HubSpot installation is
          Super-Admin only; Microsoft's `Sites.Read.All` needs admin consent). Getting one wrong
          produces a provider error naming neither the condition nor the remedy, so they are stated
          before the button rather than after the failure — the same list the setup wizard shows,
          from the same field, so the two screens cannot drift into disagreeing again. */}
      {/* ⚠️ **Always shown, never behind a disclosure**, and that is a deliberate reversal. These were
          previously collapsed behind a "What is needed" button that only appeared when the provider
          was NOT connectable — so the list was hidden in exactly the case it exists for: somebody
          about to press Connect. Being signed into the wrong portal, or lacking Super Admin, fails at
          the final step of a consent screen with a provider error that names neither the condition
          nor the remedy, and by then the advice has arrived too late to be advice.

          It stays short by construction (one or two items per provider), and it is hidden once a
          connection exists, where it would be telling somebody how to do a thing they have done. */}
      {/* ⚠️ **Our limit, before their list, and visually apart from it — B-315.**

          The setup wizard has rendered `our_limit` since it shipped; **this screen never did**, and
          this is the screen with the Connect button on it. The consequence is specific rather than
          cosmetic: the moment Google Drive becomes connectable, a client presses Connect and meets
          either Google's *"Google hasn't verified this app"* interstitial or a flat refusal for not
          being a test user — with nothing from us saying why, on the one surface they were looking at.

          It goes ABOVE the prerequisites because it can make them moot: there is no point arranging
          who will consent if nobody can yet. And it is deliberately **not** styled as one of their
          steps — F01's rule is that a screen must be clear about who is being addressed, and this
          paragraph is us talking about ourselves. */}
      {/* ⚠️ **Gated on `can_connect_now`, so this and the callout above are never both on screen.**
          Before the secrets are set, *"we have not finished setting this up"* is the truer and more
          complete statement, and two adjacent callouts in our own voice saying nearly the same thing
          is the duplicate-help-surface defect this app has already had once. Once the provider is
          connectable that sentence stops being true and this one starts. */}
      {provider.can_connect_now && provider.transport === 'rest' && mine.length === 0 && provider.our_limit ? (
        <Callout tone="info" title="Before you connect — a limit on our side">
          {provider.our_limit}
        </Callout>
      ) : null}
      {provider.transport === 'rest' && mine.length === 0 ? (
        /* ⚠️ `?? []` is not defensive clutter here. This is an installed PWA: a service worker can
           serve a cached bundle against a Worker deployed since, so "a field the current route always
           sends" and "a field this build always receives" are different claims. A crash takes out the
           whole Sources screen; an absent list costs one advisory paragraph. */
        (provider.client_prerequisites ?? []).length > 0 ? (
          <ol className="web-connect__steps">
            {(provider.client_prerequisites ?? []).map((step, i) => (
              <li key={i} className="web-connect__step">
                <p className="web-connect__steptitle">
                  {step.link ? (
                    // `noreferrer` as well as `noopener`: this points at a third-party console, and
                    // the referrer would disclose the tenant's own hostname.
                    <a href={step.link} target="_blank" rel="noopener noreferrer">
                      {step.title}
                    </a>
                  ) : (
                    step.title
                  )}
                </p>
                <p className="web-connect__stepdetail">{step.detail}</p>
              </li>
            ))}
          </ol>
        ) : (
          <HelpText>
            Nothing to arrange on your side — press Connect and approve the access we ask for.
          </HelpText>
        )
      ) : null}
    </Card>
  );
}

export function Connect({ orgId }: { orgId?: string }) {
  const [providers, setProviders] = useState<ProviderView[] | null>(null);
  const [connections, setConnections] = useState<ConnectionView[]>([]);
  const [error, setError] = useState<string | null>(null);
  /**
   * Providers the person has marked as "this is a sandbox", keyed by provider id.
   *
   * ⚠️ **Per provider, not one flag for the screen.** Somebody can hold a Salesforce sandbox and a
   * production HubSpot at once, and a single toggle would silently send the wrong host for the
   * second one.
   */
  const [sandboxFor, setSandboxFor] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    try {
      /* ⚠️ The connections list is authenticated now (B-133), so it needs the access token — held in
         memory only, never in storage (ADR-069), and fetched fresh so a stale one is refreshed rather
         than sent and refused. `/setup` stays unauthenticated on purpose: it returns the provider
         descriptors and this deployment's callback URLs, which are not secrets and are the same for
         everyone. */
      const token = await accessToken();
      const authed: HeadersInit | undefined = token ? { authorization: `Bearer ${token}` } : undefined;
      const [setup, conns] = await Promise.all([
        fetch('/api/relay/setup').then((r) => r.json() as Promise<{ providers: ProviderView[] }>),
        fetch('/api/relay/connections', { headers: authed }).then(
          (r) => r.json() as Promise<{ connections: ConnectionView[] }>
        ),
      ]);
      setProviders(setup.providers);
      setConnections(conns.connections ?? []);
      setError(null);
    } catch {
      /* Under `vite dev` there is no Worker, so these routes return the SPA shell and the JSON parse
         fails. Said plainly rather than shown as a broken screen — it is a development-mode fact, not
         a defect the reader can fix. */
      setError(
        'The connector list could not be read. If this is a local dev server without the Worker running, that is expected — the routes live in the Worker.'
      );
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Start the OAuth flow: ask our own Worker for the provider's authorize URL, then navigate there.
   *
   * Two steps rather than one navigation because the route is authenticated (B-133) and a top-level
   * navigation sends no `Authorization` header. A refusal surfaces on this screen — the Worker's own
   * message, which names whether the problem is a missing session, no organisation, or an
   * unconfigured client secret — rather than as a blank provider error page.
   */
  const begin = useCallback(async (providerId: string) => {
    setError(null);
    /* ⚠️ **The two steps live in `connectFlow.ts` now, and that move is the point.** This screen
       had the only correct copy of them; the two screens written afterwards each reached for a
       top-level `window.location.href` and got a 401 with a JSON problem document in the address
       bar. One implementation is the only version of this that cannot drift again.
       ⚠️ `orgId`, not `[]`, in the dependency list. This callback reads it, and an empty list
       would capture whichever org happened to be selected when the panel first mounted — so
       switching client and then connecting would attach the source to the *previous* one.
       Nothing on screen would say so; the records would simply arrive under the wrong client. */
    const outcome = await startConnect(providerId, { orgId, sandbox: sandboxFor[providerId] === true });
    if (!outcome.ok) setError(outcome.detail);
  }, [orgId, sandboxFor]);

  /* The callback redirects back here with an outcome in the hash. Read once and cleared, so a reload
     does not re-announce a connection that happened minutes ago. */
  const [outcome, setOutcome] = useState<{
    connected?: string;
    error?: string;
    detail?: string;
    /** Read scopes the consent screen withheld — a Degraded connection, not a failed one (US-1.3). */
    readScopeWithheld?: string;
  } | null>(null);
  useEffect(() => {
    const query = window.location.hash.split('?')[1];
    if (!query) return;
    const params = new URLSearchParams(query);
    const connected = params.get('connected') ?? undefined;
    const err = params.get('error') ?? undefined;
    if (!connected && !err) return;
    setOutcome({
      connected,
      error: err,
      detail: params.get('detail') ?? undefined,
      readScopeWithheld: params.get('read_scope_withheld') ?? undefined,
    });
    window.history.replaceState(null, '', `${window.location.pathname}#/sources`);
    void load();
  }, [load]);

  /* ⚠️ The provider's DISPLAY name, not the id from the callback URL. The callback can only carry the
     id, so these callouts read "hubspot is connected" one line above a card headed "HubSpot" — the
     kind of seam that reads as a different system rather than as the same one. Falls back to the id
     when the provider list has not arrived (or does not contain it), because a name we cannot resolve
     is better shown as the id than omitted. */
  const connectedName =
    providers?.find((p) => p.providerId === outcome?.connected)?.providerName ?? outcome?.connected;

  return (
    <div className="web-connect">
      {/* ⚠️ No `PageHeader` here. `AppShell` already renders the route's heading, so adding one gave
          the page **two `<h1>`s both reading "Sources"** — a duplicated heading is a visual defect and
          an accessibility one (a screen reader announces the page title twice and the outline has two
          roots). Found by reading the heading outline in a browser; no test asserted h1 uniqueness.
          The description lives as intro copy instead, which is what it always was. */}
      {/* ⚠️ Said *"a live connection needs an app in that system first"* until 2026-08-09 — the same
          pre-ADR-079 model as the callout below, in the very first sentence of the screen. The app
          exists already and is ours; what a connection needs from the reader is approval. */}
      <p className="web-connect__intro">
        Where your records come from. A file works immediately; a live connection asks you to approve
        read-only access to a system you already use.
      </p>

      {/* ⚠️ Tied together deliberately, not two separate facts: this app is something you INSTALL —
          onto a laptop, a desktop, wherever it will run — and then point at your own systems with
          your own credentials. That is the whole operating model, and until this line existed nothing
          on the one screen where someone is about to add a real connection ever said so. The install
          mechanics (`InstallOffer`, `pwa.ts`) already existed but were reachable only from the CSV
          screen, disconnected from the moment they matter here. */}
      <InstallOffer title="Install this once, then connect as many systems as you like">
        Installed, it opens like any other app on this device — no browser tab to find again — and
        every connection you add still runs read-only until you approve a specific write.
      </InstallOffer>

      {/* ⚠️ A partial read grant is announced as **connected but narrower**, never as success and
          never as failure. It genuinely connected and can genuinely be pulled from; what changed is
          which objects will have data. Saying only "is connected" is what sent people into a 403
          days later with no idea a box had been unticked — and saying "did not complete" would be
          false, and would invite a reconnect that was not needed. */}
      {outcome?.connected && outcome.readScopeWithheld ? (
        <Callout tone="warning" title={`${connectedName} is connected, with less access than asked for`}>
          The consent screen did not grant <code>{outcome.readScopeWithheld}</code>. Anything needing
          those permissions will have no data until you reconnect and approve them. Everything else
          works.
        </Callout>
      ) : outcome?.connected ? (
        <Callout tone="success" title={`${connectedName} is connected`}>
          You can now pull records from it. Nothing has been pulled yet.
        </Callout>
      ) : null}
      {outcome?.error ? (
        <Callout tone="danger" title="That connection did not complete">
          {/* The provider's own code, verbatim. `access_denied` and `redirect_uri_mismatch` need
              completely different responses, and flattening them to "failed" hides which. */}
          <code>{outcome.error}</code>
          {outcome.detail ? ` — ${outcome.detail}` : null}
        </Callout>
      ) : null}

      {error ? (
        <Callout tone="warning" title="Connector list unavailable">
          {error}
        </Callout>
      ) : null}

      {providers === null && !error ? (
        <Skeleton variant="rect" height="7.5rem" />
      ) : (providers ?? []).length === 0 && !error ? (
        <EmptyState title="No connectors" description="No systems are configured for this deployment." />
      ) : (
        <div className="web-connect__grid">
          {(providers ?? []).map((provider) => (
            <ProviderCard
              key={provider.providerId}
              provider={provider}
              connections={connections}
              onConnect={begin}
              sandbox={sandboxFor[provider.providerId] === true}
              onSandboxChange={(next) =>
                setSandboxFor((prev) => ({ ...prev, [provider.providerId]: next }))
              }
            />
          ))}
        </div>
      )}

      <HelpText>
        Read-only access is all any connection asks for. Permission to write is requested later, per
        action, the first time you approve one — so connecting can never change anything in your system.
      </HelpText>
    </div>
  );
}
